export const VIDEO_PROTOCOLS_V1016 = Object.freeze([
  'legacy',
  'openrouter',
  'openlux',
  'seedance',
  'kling',
  'vidu',
  'gemini_interactions',
  'veo',
  'wan',
  'minimax',
  'runway',
  'openai',
]);

const labels = {
  legacy: 'Legacy',
  openrouter: 'OpenRouter',
  openlux: 'OpenLux',
  seedance: 'Seedance / Ark',
  kling: 'Kling',
  vidu: 'Vidu',
  gemini_interactions: 'Omni Flash / Gemini',
  veo: 'Veo',
  wan: 'Wan',
  minimax: 'MiniMax',
  runway: 'Runway',
  openai: 'OpenAI (unavailable)',
};
const protocolKey = 'video_protocol_v1016';
const operationKey = 'video_operation_v1016';
export const OPENLUX_OPERATIONS_V1018 = Object.freeze([
  'video_create', 'vidu_native', 'wan_video', 'seedance_video',
  'minimax_video_v2', 'kling_text2video', 'mj_imagine',
  'luma_generate', 'kling_turbo_text2video', 'kling_turbo_image2video',
]);
const supplied = (value, key) =>
  Object.prototype.hasOwnProperty.call(value, key) && value[key] !== undefined;
const isObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const supportsVideoProtocolV1016 = (channelType) =>
  [1, '1', 8, '8', 20, '20', 40, '40', 50, '50', 52, '52'].includes(channelType);

const normalizeString = (value, field) => {
  if (value == null) return '';
  if (typeof value !== 'string') throw new Error(`Invalid ${field}.`);
  const trimmed = value.trim();
  if (
    Array.from(trimmed).some((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127;
    })
  )
    throw new Error(`Invalid ${field}.`);
  return trimmed;
};

export function normalizeVideoProtocolV1016(value) {
  const protocol = normalizeString(value, 'video protocol').toLowerCase();
  if (protocol && !VIDEO_PROTOCOLS_V1016.includes(protocol))
    throw new Error('Unsupported video protocol.');
  return protocol;
}

// Empty delegates to the backend default; no provider-native mode is guessed.
export function defaultVideoOperationV1016(protocol) {
  normalizeVideoProtocolV1016(protocol);
  return '';
}

export function videoProtocolOptionsV1016(
  channelType,
  t = (_, fallback) => fallback
) {
  if (!supportsVideoProtocolV1016(channelType)) return [];
  const protocols =
    Number(channelType) === 20 ? ['openrouter'] :
      Number(channelType) === 40 ? ['seedance'] : VIDEO_PROTOCOLS_V1016;
  return [
    {
      key: '',
      value: '',
      text: Number(channelType) === 20 ? 'OpenRouter (default)' :
        Number(channelType) === 40 ? 'Seedance / Ark (default)' :
        Number(channelType) === 52
          ? t('channel.edit.video_v1016.legacy_default', 'Legacy (default)')
          : t('channel.edit.video_v1016.unconfigured', 'Not configured'),
    },
    ...protocols.map((protocol) => ({
      key: protocol,
      value: protocol,
      text: labels[protocol],
      ...(protocol === 'openai' ? { disabled: true } : {}),
    })),
  ];
}

function readConfig(config) {
  if (config == null || config === '') return {};
  let parsed = config;
  if (typeof config === 'string') {
    try {
      parsed = JSON.parse(config);
    } catch {
      throw new Error('Invalid channel config JSON.');
    }
  }
  if (!isObject(parsed)) throw new Error('Channel config must be an object.');
  return { ...parsed };
}

function normalizeOperation(value) {
  const operation = normalizeString(value, 'video operation');
  // Match Go's byte-length bound, including existing non-ASCII mode values.
  const bytes = Array.from(operation).reduce((total, character) => {
    const point = character.codePointAt(0);
    return (
      total + (point <= 0x7f ? 1 : point <= 0x7ff ? 2 : point <= 0xffff ? 3 : 4)
    );
  }, 0);
  if (bytes > 64 || /[/\\?#]/.test(operation) || operation.includes('..'))
    throw new Error('Invalid video operation.');
  return operation;
}

export function readVideoProtocolV1016(config) {
  const parsed = readConfig(config);
  return {
    [protocolKey]: normalizeVideoProtocolV1016(parsed[protocolKey]),
    [operationKey]: normalizeOperation(parsed[operationKey]),
  };
}

// Undefined means omitted; null explicitly clears a string-valued Config field.
// Stored operations are round-tripped, not certified as provider capabilities.
export function buildVideoProtocolSubmissionV1016(
  config,
  channelType,
  patch = {}
) {
  const next = readConfig(config);
  if (!isObject(patch))
    throw new Error('Video config patch must be an object.');
  const oldProtocol = next[protocolKey];
  for (const key of [protocolKey, operationKey]) {
    if (supplied(patch, key)) next[key] = patch[key];
  }
  if (!supplied(next, protocolKey) && !supplied(next, operationKey))
    return next;
  const protocol = normalizeVideoProtocolV1016(next[protocolKey]);
  if (
    protocol &&
    (!supportsVideoProtocolV1016(channelType) ||
      (Number(channelType) === 20 && protocol !== 'openrouter') ||
      (Number(channelType) === 40 && protocol !== 'seedance'))
  )
    throw new Error('Video protocol is not supported by this channel type.');
  const previous =
    typeof oldProtocol === 'string' ? oldProtocol.trim().toLowerCase() : '';
  if (
    supplied(patch, protocolKey) &&
    previous !== protocol &&
    !supplied(patch, operationKey)
  )
    next[operationKey] = defaultVideoOperationV1016(protocol);
  if (supplied(next, protocolKey) && (!protocol || protocol === 'legacy')) {
    normalizeString(next[operationKey], 'video operation');
    next[operationKey] = '';
  }
  const operation = normalizeOperation(next[operationKey]);
  if (protocol === 'openlux' && !OPENLUX_OPERATIONS_V1018.includes(operation))
    throw new Error('Select a verified OpenLux operation.');
  if (operation && (!protocol || protocol === 'legacy'))
    throw new Error('Video operation requires an explicit provider protocol.');
  return { ...next, [protocolKey]: protocol, [operationKey]: operation };
}

export function serializeVideoProtocolConfigV1016(
  config,
  channelType,
  patch = {}
) {
  return JSON.stringify(
    buildVideoProtocolSubmissionV1016(config, channelType, patch)
  );
}
